<template>
  <div class="content">
    <div class="md-layout mobile-container">
      <h3
        class="md-headline"
        style="padding: 0 15px"
      >
        {{ isVariantMode ? 'Create Variant' : 'Edit Shortcut' }}
      </h3>
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
        <form
          class="shortcut-form"
          @submit.prevent="() => {}"
        >
          <shortcut-form
            v-if="!loading"
            :shortcut.sync="shortcut"
            :validation="$v.shortcut"
            :submitted="submitted"
            :is-variant-mode="isVariantMode"
          />

          <date-range-form
            :initial_date_interval="shortcut.date_interval"
            :enabled="shortcut.enabled"
            :validation="$v.shortcut.date_interval"
            @dateRangeUpdated="onDateRangeUpdate"
          />

          <c-t-a-form
            :cta.sync="shortcut.cta"
            :loading="loading"
          />

          <images-form
            :images.sync="shortcut.images"
            :loading="loading"
            :background_color.sync="shortcut.background_color"
          />

          <benefits-form
            :benefits.sync="shortcut.benefits"
            :loading="loading"
          />

          <actions-form
            :actions.sync="shortcut.actions"
            :loading="loading"
          />

          <md-field>
            <label>Successful Message</label>
            <md-input v-model="shortcut.successful_message" />
          </md-field>

          <label>Payload</label>
          <prism-editor
            v-model="shortcut.payload"
            :code="shortcut.payload"
            language="js"
          />
          <div
            v-if="submitted && !$v.shortcut.payload.required"
            class="error-feedback"
          >
            Field is
            required
          </div>
          <div
            v-if="submitted && !$v.shortcut.payload.isValidJSON"
            class="error-feedback"
          >
            JSON is not
            valid
          </div>

          <div class="edit-sh-columns-wrapper">
            <md-card>
              <md-card-content>
                <server-bundles-selector
                  :initial-server-bundles="shortcut.server_bundles"
                  @serverListChanged="onServerListChanged"
                />
              </md-card-content>
            </md-card>
          </div>

          <div class="edit-sh-columns-wrapper">
            <md-card>
              <md-card-content>
                <country-selector-with-continents
                  :initial-mode="shortcut['countries_access_type']"
                  :initial-iso-codes="shortcut['countries_iso_codes']"
                  :loading="loading"
                  :locked-option="isVariantMode && 'whitelist'"
                  @countryListChanged="onCountriesListChanged"
                  @modeChanged="onCountriesModeChanged"
                />
              </md-card-content>
            </md-card>

            <md-card>
              <md-card-content>
                <platforms-selector
                  :shortcut-i-d="shortcut.id"
                  :initial-mode="shortcut.platform_access_type"
                  :initial-platforms="shortcut.platforms"
                  @platformsListChanged="platformSelected"
                  @platformsModeChanged="platformModeChanged"
                />
              </md-card-content>
            </md-card>
          </div>

          <div class="edit-sh-columns-wrapper">
            <md-card>
              <md-card-content>
                <apps-selector
                  :initial-mode="shortcut.apps_access_type"
                  :initial-apps="shortcut.apps"
                  @appsListChanged="onAppsListChanged"
                  @appsModeChanged="onAppsModeChanged"
                />
              </md-card-content>
            </md-card>

            <md-card>
              <md-card-content>
                <nodes-mode-selector
                  :nodes_list_expanded="shortcut.nodes_list_expanded"
                  :group_nodes_by_country="shortcut.group_nodes_by_country"
                  :node_representation="shortcut.node_representation"
                  @listExpandedUpdate="onNodesListExtendedUpdate"
                  @groupNodesByCountryUpdate="onGroupNodesByCountryUpdate"
                  @representationUpdate="onNodesRepresentationUpdate"
                />
              </md-card-content>
            </md-card>
          </div>

          <div>
            <md-button
              class="md-success"
              @click.native="updateShortcut"
            >
              Save
            </md-button>
            <md-button
              class="md-button-content md-danger"
              @click="confirmActive = true"
            >
              Delete
            </md-button>
          </div>
        </form>
      </div>
    </div>
    <md-dialog-confirm
      class="modal-small"
      :md-active.sync="confirmActive"
      md-title="Are you sure you want to delete shortcut?"
      md-confirm-text="Yes"
      md-cancel-text="Cancel"
      @md-confirm="onConfirm"
    />

    <md-dialog
      :md-active.sync="showPreviewDialog"
      class="dialog-wrapper"
    >
      <pre
        style="overflow: scroll"
        v-html="previewDiffView"
      />

      <md-dialog-actions>
        <md-button @click.native="closePreview">
          Close
        </md-button>
        <md-button
          class="md-primary"
          @click.native="completeUpdate"
        >
          Save
        </md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>
<script>
    import "prismjs";
    import "prismjs/themes/prism-tomorrow.css";
    import PrismEditor from "vue-prism-editor";
    import {required} from "vuelidate/lib/validators";
    import {
        ActionsForm,
        BenefitsForm,
        CTAForm,
        ImagesForm,
        PlatformsSelector,
        ShortcutForm,
        AppsSelector
    } from "@/components";
    import { addUiIds } from "@/utils/uiId";
    import ServerBundlesSelector from "@/components/Forms/ServerBundlesSelector";
    import NodesModeSelector from "@/components/Forms/NodesModeSelector";
    import CountrySelectorWithContinents from "@/components/Forms/Fields/CountrySelectorWithContinents";
    import DateRangeForm from "@/components/Forms/shorcut/DateRangeForm";
    import { diff, formatters } from "jsondiffpatch";

    // import shortcut from '@/../mocks/shortcut.json';

    export default {
        components: {
            ServerBundlesSelector,
            ActionsForm,
            BenefitsForm,
            CountrySelectorWithContinents,
            CTAForm,
            ImagesForm,
            PrismEditor,
            PlatformsSelector,
            ShortcutForm,
            NodesModeSelector,
            AppsSelector,
            DateRangeForm
        },
        data() {
            return {
                loading: true,
                isVariantMode: this.$route.path.includes("variant"),

                confirmActive: false,
                errorMsg: null,
                submitted: false,

                // server bundles
                sbIndex: [],
                sbList: [],
                sbValue: "",
                sbs: [],

                showPreviewDialog: false,
                previewDiffView: "",
                fetchedJSON: {},
                preparedBody: {},

                componentKey: 0,
                showDialog: false,
                shortcut: {
                    id: "",
                    title: { en: "", uk: "" },
                    subtitle: { en: "", uk: "" },
                    cta: {
                        disconnected: { "en": "" },
                        connecting: { "en": "" },
                        connected: { "en": "" }
                    },
                    images: {
                        iconImgUrl: "https://static.provpn.world/assguard/icons/untracable@3x.png",
                        bgImgUrl: "https://static.provpn.world/assguard/backgrounds/netflixBackground.png"
                    },
                    backgroundColor: "",
                    actions: [
                        {
                          title: { "en": "" },
                          subtitle: { "en": "" },
                          imageUrl: "",
                          platform: "all",
                          url: {
                            app: "",
                            website: "",
                            store: ""
                          }
                        },
                        {
                          title: { "en": "" },
                          subtitle: { "en": "" },
                          imageUrl: "",
                          platform: "iOS",
                          url: {
                            app: "",
                            website: "",
                            store: ""
                          }
                        },
                        {
                          title: { "en": "" },
                          subtitle: { "en": "" },
                          imageUrl: "",
                          platform: "android",
                          url: {
                            app: "",
                            website: "",
                            store: ""
                          }
                        },
                        {
                          title: { "en": "" },
                          subtitle: { "en": "" },
                          imageUrl: "",
                          platform: "windows",
                          url: {
                            app: "",
                            website: "",
                            store: ""
                          }
                        }
                    ],
                    benefits: [
                        {
                          title: { "en": "" },
                          imageUrl: "",
                          platform: "all"
                        },
                        {
                          title: { "en": "" },
                          imageUrl: "",
                          platform: "iOS"
                        },
                        {
                          title: { "en": "" },
                          imageUrl: "",
                          platform: "android"
                        },
                        {
                          title : { "en": "" },
                          imageUrl: "",
                          platform: "windows"
                        }
                    ],
                    successful_message: { en: "", uk: "" },
                    payload: "{}",
                    premium: false,
                    enabled: false,
                    ageConfirmation: false,
                    slug: "",
                    apps_access_type: "common",
                    apps: [],
                    short_title: { en: "", uk: "" },
                    layout_style: "small",
                    keywords: "",
                    "platform_access_type": "common",
                    platforms: [],
                    nodes_list_expanded: false,
                    node_representation: "default_representation",
                    date_interval: {
                        enable_on_date_interval: false,
                        start_date: new Date().toISOString(),
                        end_date: new Date().toISOString()
                    },
                    sensitive: false
                }
            };
        },
        validations: {
            shortcut: {
                title: {required},
                payload: {required, isValidJSON},
                type: {required},
                slug: {
                    isValidSlugVariant(value) {
                        if (this.isVariantMode) {
                            if (!(value && this.generalSlug)) {
                                return false;
                            }
                            const [slug, suffix] = value.split("_");
                            return !!(slug && suffix
                                && slug === this.generalSlug
                                && suffix.length >= 1);
                        }
                        return true;
                    }
                },
                date_interval: {
                    start_date: {
                        isBeforeEndDate(value) {
                            if (!this.shortcut.date_interval.enable_on_date_interval) {
                                return true;
                            }
                            const start = new Date(value).getTime();
                            const end = new Date(this.shortcut.date_interval.end_date).getTime();
                            return start < end;
                        }
                    },
                    end_date: {
                        ifAfterStartDate(value) {
                            if (!this.shortcut.date_interval.enable_on_date_interval) {
                                return true;
                            }
                            const start = new Date(this.shortcut.date_interval.start_date).getTime();
                            const end = new Date(value).getTime();
                            return start < end;
                        }
                    },
                },
            },
        },
        mounted() {
            this.shortcut.id = this.$route.params.id;
            this.getShortcutById(this.shortcut.id);
        },
        methods: {
            platformSelected(e) {
                this.shortcut.platforms = e;
            },
            platformModeChanged(e) {
                this.shortcut["platform_access_type"] = e;
            },
            onCountriesModeChanged(mode) {
                if (this.isVariantMode) {
                    this.shortcut["countries_access_type"] = "whitelist";
                    return;
                }
                this.shortcut["countries_access_type"] = mode;
            },
            onCountriesListChanged(newList) {
                this.shortcut["countries_iso_codes"] = newList;
            },
            onAppsListChanged(newList) {
              this.shortcut.apps = newList;
            },
            onAppsModeChanged(mode) {
              this.shortcut["apps_access_type"] = mode;
            },
            onServerListChanged(serverList) {
              this.shortcut.server_bundles = serverList;
            },
            onNodesListExtendedUpdate(newValue) {
                this.shortcut.nodes_list_expanded = newValue;
            },
            onGroupNodesByCountryUpdate(newValue) {
                this.shortcut.group_nodes_by_country = newValue;
            },
            onNodesRepresentationUpdate(newValue) {
                this.shortcut.node_representation = newValue;
            },
            onDateRangeUpdate(newValue) {
                this.shortcut.date_interval = newValue;
            },
            onConfirm() {
                this.deleteShortcut();
            },
            showPreview() {
                this.showPreviewDialog = true;

                const d = diff(this.fetchedJSON, this.preparedBody);

                this.previewDiffView = formatters.html.format(d, this.fetchedJSON);

                if (this.previewDiffView.length === 0) {
                    this.previewDiffView = "No differences found\n\r" + JSON.stringify(this.fetchedJSON, null, 4);
                }
            },
            closePreview() {
                this.showPreviewDialog = false;
            },
            deleteShortcut() {
                this.$http.delete("/v1/admin/shortcut/"+ this.shortcut.id).then(
                    () => {
                        this.$notify({
                            group: "foo",
                            title: "Shortcut deleted",
                        });

                        this.$router.push("/shortcuts");
                    },
                    err => {
                        console.error(err);
                    });
            },
            // TODO: remove workarounds
            getShortcutById(id) {
                // Promise.resolve({ data: { shortcut }}).then(
                this.$http.get("/v1/admin/shortcut?id=" + id).then(
                    res => {
                        let sc = res.data.shortcut;
                        this.fetchedJSON = Object.fromEntries(
                            Object.entries(sc).filter(
                                ([key]) => !["analytics_id", "curated", "marketing_type", "notice"].includes(key)
                            )
                        );
                        this.fetchedJSON.nodes = this.fetchedJSON.nodes.map(n => n.id);
                        this.fetchedJSON = JSON.parse(JSON.stringify(this.fetchedJSON));

                        let nodeIds = [];

                        sc.nodes.forEach(el => nodeIds.push(el.id));

                        this.shortcut = {
                            id: sc.id,
                            title: sc.title,
                            subtitle: sc.subtitle,
                            images: sc.images,
                            background_color: sc.background_color,
                            actions: sc.actions,
                            benefits: sc.benefits,
                            successful_message: sc.successful_message,
                            payload: JSON.stringify(sc.payload, null, 4),
                            nodes: JSON.stringify(nodeIds, null, 4),
                            cta: sc.cta,
                            enabled: sc.enabled,
                            premium: sc.premium,
                            requires_age_confirmation: sc.requires_age_confirmation,
                            type: sc.type,
                            index: sc.index,
                            slug: sc.slug,
                            read_more_link: sc.read_more_link,
                            group_nodes_by_country: sc.group_nodes_by_country,
                            category: sc.category.en,
                            comment: sc.comment,
                            "countries_access_type": sc["countries_access_type"],
                            "countries_iso_codes": sc["countries_iso_codes"],
                            apps: sc.apps,
                            "apps_access_type": sc["apps_access_type"],
                            short_title: sc.short_title,
                            layout_style: sc.layout_style,
                            keywords: sc.keywords,
                            "platform_access_type": sc.platform_access_type,
                            platforms: sc.platforms,
                            server_bundles: sc.server_bundles,
                            nodes_list_expanded: sc.nodes_list_expanded,
                            node_representation: sc.node_representation,
                            date_interval: sc.date_interval,
                            sensitive: sc.sensitive,
                            tags: []
                        };

                        if (sc.nodes.length > 0) {
                            this.shortcut.nodeId = sc.nodes[0].id;
                        }

                        if (!sc.cta.connected && !sc.cta.connecting && !sc.cta.disconnected) {
                            this.shortcut.cta = {
                                disconnected: { "en": "" },
                                connecting: { "en": "" },
                                connected: { "en": "" }
                            };
                        }

                        // adds a uiId to each element to have smth to use `:key` on
                        this.shortcut.actions = this.shortcut.actions.map(addUiIds);
                        this.shortcut.benefits = this.shortcut.benefits.map(addUiIds);

                        if (this.isVariantMode) {
                            // @helenushka's requests
                            this.shortcut.enabled = false;
                            this.shortcut.comment = "";
                            // saving original slug to validate against it
                            this.generalSlug = this.shortcut.slug;

                            this.shortcut["countries_access_type"] = "whitelist";
                            this.shortcut["countries_iso_codes"] = [];
                        }

                        this.loading = false;
                        document.title = this.isVariantMode
                            ? `${this.shortcut.slug} Create Variant - Admin panel`
                            : `${this.shortcut.slug} Edit Shortcut - Admin panel`;
                    },
                    err => {
                        console.error(err);
                    },
                );
            },
            updateShortcut() {
                this.submitted = true;
                this.errorMsg = null;

                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    return;
                }

                let body = {
                    ...this.shortcut,

                    id: this.shortcut.id,
                    title: this.shortcut.title,
                    subtitle: this.shortcut.subtitle,
                    category: {
                        "en": this.shortcut.category,
                    },
                    successful_message: this.shortcut.successful_message,
                    background_color: this.shortcut.background_color,
                    images: this.shortcut.images,
                    actions: this.shortcut.actions.map(a => {
                        delete a.uiId;
                        return a;
                    }),
                    benefits: this.shortcut.benefits.map(b => {
                        delete b.uiId;
                        return b;
                    }),
                    read_more_link: this.shortcut.read_more_link,
                    group_nodes_by_country: this.shortcut.group_nodes_by_country,
                    payload: JSON.parse(this.shortcut.payload.replace(/(\r\n|\n|\r)/gm, "")),
                    cta: this.shortcut.cta,
                    server_bundles: this.shortcut.server_bundles,
                    nodes: JSON.parse(this.shortcut.nodes.replace(/(\r\n|\n|\r)/gm, "")),
                    premium: this.shortcut.premium,
                    enabled: this.shortcut.enabled,
                    requires_age_confirmation: this.shortcut.requires_age_confirmation,
                    type: this.shortcut.type,
                    index: parseInt(this.shortcut.index, 10),
                    slug: this.shortcut.slug,

                    platform_access_type: this.shortcut["platform_access_type"],
                    platforms: this.shortcut.platforms,

                    countries_iso_codes: this.shortcut["countries_iso_codes"],
                    countries_access_type: this.shortcut["countries_access_type"],
                    comment: this.shortcut.comment,

                    apps: this.shortcut.apps,
                    apps_access_type: this.shortcut["apps_access_type"],

                    short_title: this.shortcut.short_title,
                    layout_style: this.shortcut.layout_style,
                    keywords: this.shortcut.keywords,

                    nodes_list_expanded: this.shortcut.nodes_list_expanded,
                    node_representation: this.shortcut.node_representation,

                    date_interval: this.shortcut.date_interval,
                    sensitive: this.shortcut.sensitive,
                };

                this.preparedBody = body;
                this.showPreview();
            },
            completeUpdate() {
                this.closePreview();

                const body = this.preparedBody;
                if (this.isVariantMode) {
                    this.$http.post("/v1/admin/shortcut/create", body)
                        .then(() => this.$http.get("/v1/admin/shortcut?id=" + this.shortcut.id))
                        .then(res => {
                            const s = res.data.shortcut;

                            s.countries_access_type = "blacklist";
                            s.countries_iso_codes = s.countries_iso_codes.concat(this.shortcut.countries_iso_codes);

                            return this.$http.post("/v1/shortcut/update", s);
                        })
                        .then(
                            () => {
                                this.$notify({
                                    group: "foo",
                                    title: "Shortcut variant created",
                                });

                                this.$router.push("/shortcuts");
                            },
                            err => {
                                console.error(err);
                            },
                        );
                } else {
                    this.$http.post("/v1/shortcut/update", body).then(
                        () => {
                            this.$notify({
                                group: "foo",
                                title: "Shortcut updated",
                            });

                            this.$router.push("/shortcuts");
                        },
                        err => {
                            console.error(err);
                        },
                    );
                }
            },
            toggleNodeDialog() {
                this.showDialog = !this.showDialog;
            },
        }
    };

    function isValidJSON(str) {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }

        return true;
    }
</script>

<style lang="scss" scoped>
    .shake-on-error .md-duplicated {
        animation-name: shake;
        animation-duration: 0.5s;
    }

    .dialog-wrapper .md-dialog-container {
      max-width: 100%;
    }

    @keyframes shake {
        0% {
            transform: translate(15px);
        }
        20% {
            transform: translate(-15px);
        }
        40% {
            transform: translate(7px);
        }
        60% {
            transform: translate(-7px);
        }
        80% {
            transform: translate(3px);
        }
        100% {
            transform: translate(0px);
        }
    }

    .pulse-on-error >>> .md-duplicated {
        animation-name: pulse;
        animation-duration: 0.5s;
        animation-iteration-count: infinite;
        animation-direction: alternate;
        animation-timing-function: ease-in-out
    }

    @keyframes pulse {
        0% {
            transform: scale(1.1, 1.1);
        }
        100% {
            transform: scale(0.9, 0.9);
        }
    }

    .error-feedback {
        font-size: 12px;
        color: red;
        margin-right: 5px;
    }

    .shortcut-form {
        max-width: 1000px;
    }

    .edit-sh-columns-wrapper {
        display: flex;
        flex-direction: row;

        div ~ div {
            margin-left: 15px;
        }
    }

    @media only screen and (max-width: 500px) {
      .edit-sh-columns-wrapper {
        flex-direction: column;

        div ~ div {
          margin-left: 0;
        }
      }
    }

    @media only screen
    and (max-width: 850px) {
        .mobile-container {
            padding: 0 !important;
        }
        .edit-sh-columns-wrapper {
            flex-direction: column;
        }
    }
    @-webkit-media only screen and (max-width: 850px) {
        .mobile-container {
            padding: 0 !important;
        }
        .edit-sh-columns-wrapper {
            flex-direction: column;
        }
    }
</style>
